import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import { readURIParams } from "@flixbus-phx/marketplace-common";
import { useSearchParams } from "react-router-dom";
import addGetParams from "../../shared/helpers/addGetParams/addGetParams";
import { PageSize } from "../../shared/types/schema";

export type Pagination = {
  page: number;
  pageSize: PageSize;
};

export const getDefaultPaginationValues = (): Pagination => {
  return {
    page: 0,
    pageSize: PageSize.Fifty,
  };
};

export const getPage = (): Pagination["page"] => {
  const { page } = readURIParams(window.location.search);
  if (page && !Number.isNaN(parseInt(page, 10))) {
    return parseInt(page, 10) - 1;
  }
  return getDefaultPaginationValues().page;
};

export const getPageSize = (): Pagination["pageSize"] => {
  const { pageSize } = readURIParams(window.location.search);

  if (pageSize && Object.values(PageSize).includes(pageSize as PageSize)) {
    return pageSize as PageSize;
  }
  return getDefaultPaginationValues().pageSize;
};

export const paginationVar = makeVar({
  page: getPage(),
  pageSize: getPageSize(),
});

export const useResetPagination = (reactiveVar: ReactiveVar<Pagination>) => {
  const pagination = useReactiveVar(reactiveVar);
  return () => {
    const defaultValue = getDefaultPaginationValues();
    if (JSON.stringify(pagination) !== JSON.stringify(defaultValue)) {
      reactiveVar(defaultValue);
    }
  };
};

const usePagination = (
  reactiveVar: ReactiveVar<Pagination>
): [Pagination, (changedPaginationValues: Pagination) => void] => {
  const pagination = useReactiveVar(reactiveVar);
  const [searchParams, setSearchParams] = useSearchParams();

  const setParams = (changedPaginationValues: Pagination) => {
    addGetParams(
      {
        page: (changedPaginationValues.page + 1).toString(),
        pageSize: changedPaginationValues.pageSize,
      },
      searchParams
    );
    setSearchParams(searchParams);
  };

  const setPagination = (changedPaginationValues: Pagination) => {
    reactiveVar({
      ...changedPaginationValues,
    });
    setParams(changedPaginationValues);
  };

  return [pagination, setPagination];
};

export default usePagination;
